import React, { useEffect, useState } from 'react';
import {
	Form,
	Input,
	Button,
	message,
	Typography,
	Steps,
	Switch,
	Select,
	Radio,
	Spin,
	Alert,
	Checkbox,
} from 'antd';
import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { getAllUserEventTypes } from '@/api/calendars/calcom';
import { getHousecallproTemplate } from '@/api/calendars/housecallpro';
import { getUser, updateUser } from '@/api/user';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Video } from 'lucide-react';
import ZoomLogo from '@/assets/images/calendars/zoom.png';
import GoogleMeetLogo from '@/assets/images/calendars/meet.png';
import CalComLogo from '@/assets/images/calendars/calcom.png';
import GohighlevelLogo from '@/assets/images/calendars/gohighlevel.png';
import HousecallproLogo from '@/assets/images/calendars/housecallpro.png';
import { FaApple } from 'react-icons/fa';
import { PiMicrosoftOutlookLogoFill } from 'react-icons/pi';
import { SiGooglecalendar } from 'react-icons/si';
import './CalendarSetupForm.scss';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal } from '@/store/modal/slice';
import {
	checkGoHighLevelConnection,
	createGoHighLevelOAuthState,
	fetchGhlCalendars,
	getGoHighLevelAuthUrl,
	openOAuthWindow,
} from '@/pages/ReceptionistDashboard/ApiWorkflowTemplate/GoHighLevelIntegration';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import { WorkflowType } from 'src/enums/EWorkflow';
import { safeJSONParse } from '@/utils/helper';
import { useFeatureGating } from 'src/Hooks/useFeatureGating';
import { EFeatureCategories } from 'src/enums/EFeatureCategories';
import { EFeatureKeys } from 'src/enums/EFeatureKeys';

const { Text } = Typography;

interface CalendarSetupFormProps {
	isLoading?: boolean;
	onFinish: (values: any) => void;
	onCancel: () => void;
	existingCalendars?: any;
	initialValues?: any;
	isDuplicate?: boolean;
}

interface EventType {
	id: string;
	name: string;
	locations?: any[];
	bookingFields?: Array<{
		name: string;
		type: string;
		defaultLabel?: string;
		required?: boolean;
		editable?: string;
		sources?: Array<{
			id: string;
			type: string;
			label: string;
		}>;
	}>;
}

export const CalendarSetupForm: React.FC<CalendarSetupFormProps> = ({
	onFinish,
	existingCalendars,
	initialValues,
	isDuplicate = false,
}) => {
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [eventTypes, setEventTypes] = useState<EventType[]>([]);
	const [selectedEventLocations, setSelectedEventLocations] = useState<{ [key: number]: string[] }>(
		{},
	);
	const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
	const [selectedCalendarType, setSelectedCalendarType] = useState<string | null>(null);
	const [ghlCalendars, setGhlCalendars] = useState<any[]>([]);
	const [isLoadingGhlCalendars, setIsLoadingGhlCalendars] = useState(false);
	const [isGhlConnected, setIsGhlConnected] = useState(false);
	// const isGohighlevelIntegrationEnabled = useFeatureFlag(FeatureFlag.GOHIGHLEVEL_INTEGRATION);
	const redirectURL = import.meta.env.VITE_APP_API_BASE_URL + '/api/v1/gohighlevel/auth/callback';
	const location = Form.useWatch(['calendars', 0, 'location'], form);
	const additionalUserInfoFields =
		Form.useWatch(['calendars', 0, 'additionalUserInfoFields'], form) || [];
	const { isFeatureEnabled } = useFeatureGating();

	useEffect(() => {
		if (form) {
			form.validateFields([['calendars', 0, 'customFields']]).catch(() => {});
		}
	}, [additionalUserInfoFields, form]);

	// Get the logged in user for GoHighLevel integration
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const canAddScenario = useAppSelector(selectCanAddScenario);
	const username = selectedAgent?.number || '';
	const resellerEmailCheck = loggedInUser?.resellerClient ? 'RESELLER' : 'RECEPTIONIST';
	const isAPIWorkflowEnabled = isFeatureEnabled(
		EFeatureKeys.API_WORKFLOWS,
		EFeatureCategories.WORKFLOWS,
	);

	const { refetch: refetchUser } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});

	// Mutation for API workflow creation
	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onError: (error) => {
			message.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
		},
	});

	// Helper function to process headers with API key
	const processHeaders = (headers: any[], apiKeyValue: string) => {
		if (!headers || !Array.isArray(headers)) {
			return [];
		}

		return headers.map((header) => {
			if (header && typeof header.value === 'string' && header.value.includes('{{apikey}}')) {
				return {
					...header,
					value: header.value.replace('{{apikey}}', apiKeyValue),
				};
			}
			return header;
		});
	};

	// Helper function to process additional APIs
	const processAdditionalApis = (apis: any[], apiKeyValue: string) => {
		if (!apis || !Array.isArray(apis)) {
			return [];
		}

		return apis.map((api) => ({
			...api,
			headers: processHeaders(api.headers || [], apiKeyValue),
		}));
	};

	// Create Housecall Pro workflow function
	const createHousecallProWorkflow = async (
		apiKey: string,
		selectedFeatures: string[],
		appointmentDuration: number,
	) => {
		setLoading(true);

		try {
			// Get the template data for Housecall Pro
			const templateData: any = await getHousecallproTemplate('HousecallPro');

			if (!templateData || !Array.isArray(templateData) || templateData.length === 0) {
				throw new Error('Failed to fetch Housecall Pro template data');
			}

			// Get selected feature IDs
			const selectedFeatureIds: number[] = [];
			const parsedFeatures = safeJSONParse(templateData[0]?.features, []);

			parsedFeatures.forEach((feature: any) => {
				if (selectedFeatures.includes(feature.name)) {
					selectedFeatureIds.push(...feature.ids);
				}
			});

			// Filter APIs based on selected feature IDs
			const apiList = templateData[0].api || [];
			const filteredApis = apiList.filter((api: any) => {
				const apiId = typeof api.id === 'string' ? parseInt(api.id, 10) : api.id;
				return selectedFeatureIds.some((id) => {
					const selectedId = typeof id === 'string' ? parseInt(id as string, 10) : id;
					return apiId === selectedId;
				});
			});

			// Format workflows
			const formattedWorkflows = filteredApis.map((api: any) => {
				let extractedCallInfo = api.extractedCallInfo;

				if (extractedCallInfo) {
					extractedCallInfo = extractedCallInfo.map((item: any) => {
						if (item.name === 'end_time') {
							return {
								...item,
								description: `Do not ask end date time form the caller. Add ${appointmentDuration} hour${appointmentDuration !== 1 ? 's' : ''} from start datetime.`,
							};
						}

						if (item.name === 'new_end_time') {
							return {
								...item,
								description: `Do not ask new end date time form the caller. Add ${appointmentDuration} hour${appointmentDuration !== 1 ? 's' : ''} from new start datetime.`,
							};
						}

						return item;
					});
				}

				return {
					id: api.id || String(Date.now()),
					name: api.name,
					workflowType: api.workflowType || 1,
					scenario: api.scenario || null,
					webhookInformation: {
						url: api.webhookInformation?.url,
						method: api.webhookInformation?.method || 'GET',
						body: api.webhookInformation?.body || '',
						headers: processHeaders(api.webhookInformation?.headers || [], apiKey),
						responseData: api.webhookInformation?.responseData || [],
					},
					authApi: {},
					isQueryParams: api.isQueryParams || false,
					isURLEncodedApi: api.isURLEncodedApi || false,
					isXChecksum: api.isXChecksum || false,
					xChecksumSalt: api.xChecksumSalt || '',
					xChecksumAlgorithm: api.xChecksumAlgorithm || '',
					xChecksumPayloadSorted: api.xChecksumPayloadSorted || false,
					extractedCallInfo,
					extractFromContext: api.extractFromContext || [],
					systemVariable: api.systemVariable || [],
					timeoutSeconds: api.timeoutSeconds || 10,
					speechToEmit: api.speechToEmit || '',
					isRepeatable: api.isRepeatable || false,
					postExecutionPrompt: api.postExecutionPrompt || '',
					requiresAuthToken: false,
					prompt: api.prompt || '',
					additionalApis: processAdditionalApis(api.additionalApis || [], apiKey),
				};
			});
			const loadingKey = 'saving-housecallpro-integration';
			message.loading({
				content: 'Saving Housecall Pro integration...',
				key: loadingKey,
				duration: 0,
			});

			// Save integration with the formatted workflows
			return new Promise((resolve, reject) => {
				mutation.mutate(
					{
						call_webhook_workflows: JSON.stringify(formattedWorkflows),
						agentId: selectedAgentId as string,
					},
					{
						onSuccess: async () => {
							setLoading(false);
							message.success({
								content: 'Open “API Integration” to manage your integrations!',
								key: loadingKey,
								duration: 5,
							});

							await refetchUser();
							window.dispatchEvent(new CustomEvent('api-workflows-updated'));

							resolve(true);
						},
						onError: (error: { message: any }) => {
							setLoading(false);
							message.error({
								content: `Failed to save integration: ${error instanceof Error ? error.message : 'Unknown error'}`,
								key: loadingKey,
								duration: 5,
							});
							reject(error);
						},
					},
				);
			});
		} catch (error) {
			console.error('Error creating Housecall Pro workflows:', error);
			message.error('Failed to create Housecall Pro workflows');
			setLoading(false);
			throw error;
		}
	};

	useEffect(() => {
		if (selectedServiceType === 'gohighlevel') {
			checkGhlConnection();
		}
	}, [selectedServiceType]);

	useEffect(() => {
		if (isDuplicate && initialValues?.data?.calendars?.[0]) {
			const calendar = initialValues.data.calendars[0];

			if (calendar.nameOfCalendar === 'gohighlevel') {
				setSelectedServiceType('gohighlevel');
			} else {
				setSelectedServiceType('calcom');
				if (calendar.location?.includes('google')) {
					setSelectedCalendarType('google');
				} else if (calendar.location?.includes('outlook')) {
					setSelectedCalendarType('outlook');
				} else if (calendar.location?.includes('apple')) {
					setSelectedCalendarType('apple');
				}
			}

			form.setFieldsValue({
				calendars: [
					{
						apiKey: calendar.accessToken || calendar.apiKey,
						scenario:
							calendar.scenario || 'Whenever the caller wants to book or schedule any meetings.',
						eventTypeId: calendar.eventTypeId,
						calendarSelected: calendar.calendarSelected,
						location: calendar.location,
						sendCallerSMSNotification: calendar.sendCallerSMSNotification !== false,
						sendOwnerSMSNotification: calendar.sendOwnerSMSNotification !== false,
						customFields: calendar.customFields || [],
					},
				],
			});

			if (calendar.nameOfCalendar === 'gohighlevel') {
				setCurrentStep(2);
			} else {
				setCurrentStep(2);
			}
		}
	}, [isDuplicate, initialValues]);

	const checkGhlConnection = async () => {
		if (!username) {
			message.error('User not logged in. Please login again.');
			return;
		}

		try {
			const connectionData = await checkGoHighLevelConnection({
				resellerEmailCheck,
				agentId: selectedAgentId as string,
			});
			setIsGhlConnected(connectionData?.isConnected || false);

			if (connectionData?.isConnected) {
				message.success('Already connected to GoHighLevel');
				const cal = await fetchGhlCalendars({
					resellerEmailCheck,
					agentId: selectedAgentId as string,
				});
				setGhlCalendars(cal || []);
			}
		} catch (error) {
			console.error('Error checking GoHighLevel connection:', error);
		} finally {
			setLoading(false);
		}
	};

	// Fetch GoHighLevel calendars
	const fetchGhlCalendarsData = async () => {
		try {
			setIsLoadingGhlCalendars(true);
			const calendars = await fetchGhlCalendars({
				resellerEmailCheck,
				agentId: selectedAgentId as string,
			});

			if (calendars && Array.isArray(calendars)) {
				setGhlCalendars(calendars);
			}
		} catch (error) {
			console.error('Error fetching GHL calendars:', error);
			message.error('Failed to fetch GoHighLevel calendars');
		} finally {
			setIsLoadingGhlCalendars(false);
		}
	};
	useEffect(() => {
		const handleOAuthComplete = (event: CustomEvent) => {
			const detail = event.detail || {};
			if (detail.source === 'gohighlevel' && detail.success) {
				setIsGhlConnected(true);
				fetchGhlCalendarsData();
			}
		};
		window.addEventListener('oauth-complete', handleOAuthComplete as EventListener);
		return () => {
			window.removeEventListener('oauth-complete', handleOAuthComplete as EventListener);
		};
	}, [fetchGhlCalendarsData]);

	// Connect to GoHighLevel
	const handleGhlConnect = async () => {
		if (!username || !resellerEmailCheck) {
			return;
		}
		try {
			const currentPath = window.location.pathname;
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.set('integration', 'gohighlevel');

			// Create OAuth state parameter
			const state = createGoHighLevelOAuthState(
				username,
				resellerEmailCheck,
				currentPath,
				searchParams,
			);

			// Get the authorization URL
			const authUrl = await getGoHighLevelAuthUrl(username, resellerEmailCheck, redirectURL, state);

			openOAuthWindow(authUrl, true, 'gohighlevel');
		} catch (error) {
			console.error('Failed to get GoHighLevel authorization URL:', error);
			message.error('Failed to connect to GoHighLevel. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Handle Cal.com connection
	const handleCalcomConnect = async () => {
		try {
			const values = await form.validateFields(['calendars', 0, 'apiKey']);
			const apiKey = values.calendars[0].apiKey;

			if (!apiKey) {
				message.error('Please enter an API key');
				return;
			}

			setLoading(true);

			const data: any = await getAllUserEventTypes({ apiKey });
			console.info('Raw event types data:', data);

			if (data && data.length > 0) {
				// Filter out event types that are already used in existing calendars
				const usedEventTypeIds = Array.isArray(existingCalendars)
					? existingCalendars.map((calendar) => calendar.eventTypeId)
					: [];

				const fetchedEventTypes = data
					.filter((event: any) => !usedEventTypeIds.includes(event.id))
					.map((event: any) => {
						console.info(
							'Processing event:',
							event.title,
							'with booking fields:',
							event.bookingFields,
						);

						// Process booking fields if they exist
						let customFields: any[] = [];
						if (event.bookingFields && Array.isArray(event.bookingFields)) {
							// Fields to skip
							const skipFields = [
								'name',
								'email',
								'location',
								'notes',
								'guests',
								'rescheduleReason',
							];

							// Process each booking field
							customFields = event.bookingFields
								.filter((field: any) => {
									const shouldSkip = skipFields.includes(field.name);
									console.info(`Field ${field.name} - should skip: ${shouldSkip}`);
									return !shouldSkip;
								})
								.map((field: any) => {
									const customField = {
										name: field.name,
										description: `Ask the ${field.defaultLabel || field.name}`,
									};
									console.info('Created custom field:', customField);
									return customField;
								});

							// Remove duplicates based on name
							customFields = customFields.filter(
								(field, index, self) => index === self.findIndex((f) => f.name === field.name),
							);
							console.info('Final custom fields after deduplication:', customFields);
						}

						return {
							id: event.id,
							name: event.title,
							locations: event.locations,
							bookingFields: event.bookingFields, // Store the original booking fields
							customFields,
						};
					});

				if (fetchedEventTypes.length === 0) {
					message.warning(
						'All event types are already in use. Please create a new event type in Cal.com.',
					);
					return;
				}

				setEventTypes(fetchedEventTypes);
				message.success('Successfully connected to Cal.com!');

				// Set default values for the newly connected calendar
				const calendars = form.getFieldValue('calendars');
				const firstEventType = fetchedEventTypes[0];
				console.info('Setting up calendar with first event type:', firstEventType);

				calendars[0] = {
					...calendars[0],
					connected: true,
					eventTypeId: calendars[0]?.eventTypeId || firstEventType?.id || '',
					location: calendars[0]?.location || firstEventType?.locations?.[0]?.type || '',
					customFields: firstEventType?.customFields || [],
				};

				console.info('Updated calendar values:', calendars[0]);
				form.setFieldsValue({ calendars });

				// Update locations for the selected event type
				const eventTypeId = calendars[0].eventTypeId;
				const selectedEvent = fetchedEventTypes.find((et: { id: string }) => et.id === eventTypeId);

				if (selectedEvent?.locations) {
					const locations = selectedEvent.locations.map((loc: any) => loc.type);
					setSelectedEventLocations((prev) => ({
						...prev,
						0: locations,
					}));
				} else if (fetchedEventTypes[0]?.locations) {
					const locations = fetchedEventTypes[0].locations.map((loc: any) => loc.type);
					setSelectedEventLocations((prev) => ({
						...prev,
						0: locations,
					}));
				}

				// Move to Event Type step after successful connection
				setCurrentStep(2);
			} else {
				message.error('No event types found. Please check your API key');
			}
		} catch (error) {
			console.error('Error connecting to Cal.com:', error);
			message.error('Failed to fetch event types. Please check your API key');
		} finally {
			setLoading(false);
		}
	};

	const handleHousecallproConnect = async () => {
		try {
			const values = await form.validateFields(['calendars', 0, 'apiKey']);
			const apiKey = values.calendars[0].apiKey;

			if (!apiKey) {
				message.error('Please enter an API key');
				return;
			}

			setLoading(true);

			try {
				const data: any = await getHousecallproTemplate('HousecallPro');

				if (data && Array.isArray(data) && data.length > 0 && data[0].features) {
					const featuresStr = data[0].features;
					const parsedFeatures = safeJSONParse(featuresStr, featuresStr);

					console.info('Parsed Housecallpro features:', parsedFeatures);

					if (Array.isArray(parsedFeatures)) {
						const formattedFeatures = parsedFeatures.map((feature: any) => ({
							id: feature.name,
							name: feature.name,
							field: feature.field,
							ids: feature.ids,
						}));

						setEventTypes(formattedFeatures);

						const calendars = form.getFieldValue('calendars');
						calendars[0] = {
							...calendars[0],
							connected: true,
							apiKey,
							appointmentDuration: 2,
							eventTypeId: formattedFeatures.length > 0 ? formattedFeatures[0].name : '',
							selectedFeatures: formattedFeatures.length > 0 ? [formattedFeatures[0].id] : [],
						};
						form.setFieldsValue({ calendars });
						message.success('Successfully connected to Housecall Pro!');
						setLoading(false);
						setCurrentStep(2);
						return;
					} else {
						throw new Error('Features is not an array');
					}
				} else {
					console.warn('No features found in the response:', data);
					message.warning('No features found in the Housecall Pro template');
				}
			} catch (error) {
				console.error('Error processing Housecall Pro data:', error);
				message.error(
					error instanceof Error ? error.message : 'Failed to process Housecall Pro template',
				);
			}
		} catch (error) {
			console.error('Error connecting to Housecallpro:', error);
			message.error('Failed to fetch event types. Please check your API key');
		}

		setLoading(false);
	};
	const handleEventTypeChange = (value: string) => {
		if (selectedServiceType === 'gohighlevel') {
			const calendars = form.getFieldValue('calendars');
			const selectedCalendar = ghlCalendars.find((calendar) => calendar.id === value);

			calendars[0] = {
				...calendars[0],
				eventTypeId: value,
				calendarSelected: selectedCalendar?.name || '',
			};
			form.setFieldsValue({ calendars });
			return;
		}

		const eventType = eventTypes.find((event) => event.id === value);
		if (eventType) {
			const calendars = form.getFieldValue('calendars');

			let customFields: any[] = [];
			if (eventType.bookingFields && Array.isArray(eventType.bookingFields)) {
				console.info(
					'Processing booking fields for event type:',
					eventType.name,
					eventType.bookingFields,
				);

				const skipFields = ['name', 'email', 'location', 'notes', 'guests', 'rescheduleReason'];

				customFields = eventType.bookingFields
					.filter((field: any) => {
						const shouldSkip = skipFields.includes(field.name);
						console.info(`Field ${field.name} - should skip: ${shouldSkip}`);
						return !shouldSkip;
					})
					.map((field: any) => {
						const customField = {
							name: field.name,
							description: `Ask the ${field.defaultLabel || field.name}`,
						};
						console.info('Created custom field:', customField);
						return customField;
					});

				// Remove duplicates based on name
				customFields = customFields.filter(
					(field, index, self) => index === self.findIndex((f) => f.name === field.name),
				);
				console.info('Final custom fields after deduplication:', customFields);
			}

			calendars[0] = {
				...calendars[0],
				eventTypeId: value,
				location: eventType.locations?.[0]?.type || null,
				customFields,
			};
			console.info('Updated calendar values with new event type:', calendars[0]);
			form.setFieldsValue({ calendars });

			if (eventType.locations) {
				const locations = eventType.locations.map((loc: any) => loc.type);
				setSelectedEventLocations((prev) => ({
					...prev,
					0: locations,
				}));
			}
		}
	};

	const handleNextStep = async () => {
		try {
			let fieldsToValidate: string[] = [];

			switch (currentStep) {
				case 0:
					if (selectedServiceType === 'calcom' && !selectedCalendarType) {
						message.error('Please select a calendar type');
						return;
					}
					setCurrentStep(1);
					break;
				case 1:
					if (selectedCalendarType === 'gohighlevel') {
						if (!isGhlConnected) {
							message.error('Please connect to GoHighLevel first');
							return;
						}
						setCurrentStep(2);
					} else {
						fieldsToValidate = ['calendars', '0', 'apiKey'];
					}
					break;
				case 2:
					if (selectedServiceType === 'housecallpro') {
						fieldsToValidate = ['calendars', '0', 'scenario', 'calendars', '0', 'selectedFeatures'];

						const selectedFeatures = form.getFieldValue(['calendars', 0, 'selectedFeatures']) || [];
						if (selectedFeatures.length === 0) {
							message.error('Please select at least one feature');
							return;
						}
						await form.validateFields(fieldsToValidate);
						setCurrentStep(3);
						return;
					} else {
						fieldsToValidate = ['calendars', '0', 'eventTypeId'];
					}
					break;
				case 3:
					if (selectedServiceType === 'housecallpro') {
						try {
							await form.validateFields([
								'calendars',
								'0',
								'apiKey',
								'calendars',
								'0',
								'selectedFeatures',
							]);

							await handleSubmit();
							return;
						} catch (error) {
							console.error('Validation failed for Housecall Pro:', error);
							message.error('Please check all fields and try again');
							return;
						}
					}
					if (selectedServiceType === 'calcom') {
						// Cal.com validation
						fieldsToValidate = ['calendars', '0', 'location'];
					}
					break;
			}

			if (fieldsToValidate.length > 0) {
				await form.validateFields(fieldsToValidate);
			}

			if (currentStep === 3 && selectedServiceType !== 'housecallpro') {
				await handleSubmit();
			} else if (currentStep < 3 || selectedServiceType !== 'housecallpro') {
				setCurrentStep(currentStep + 1);
			}
		} catch (error) {
			console.error('Step validation failed:', error);
			if (error instanceof Error) {
				message.error(error.message);
			} else {
				message.error('Please fill in all required fields');
			}
		}
	};

	const getLocationDisplayName = (locationType: string) => {
		switch (locationType) {
			case 'integrations:google:meet':
				return 'Google Meet';
			case 'integrations:zoom':
				return 'Zoom';
			case 'integrations:daily':
				return 'Cal.com';
			default:
				return locationType.split(':').pop() || locationType;
		}
	};

	const getLocationIcon = (locationType: string) => {
		switch (locationType) {
			case 'integrations:google:meet':
				return <img src={GoogleMeetLogo} alt="Google Meet" className="w-5 h-5" />;
			case 'integrations:zoom':
				return <img src={ZoomLogo} alt="Zoom" className="w-5 h-5" />;
			case 'integrations:daily':
				return <img src={CalComLogo} alt="Cal.com" className="w-5 h-5" />;
			default:
				return <Video className="w-5 h-5" />;
		}
	};

	const handleSubmit = async () => {
		try {
			if (!canAddScenario) {
				message.error(
					'You have reached the maximum number of scenarios (100). Please delete some existing scenarios before adding new ones.',
				);
				return;
			}
			const values = await form.validateFields();

			const calendar = values.calendars[0];
			if (!calendar) {
				message.error('No calendar configuration found');
				return;
			}
			if (selectedServiceType === 'gohighlevel') {
				if (!calendar.eventTypeId) {
					message.error('Please select a calendar');
					return;
				}

				const selectedCalendar = ghlCalendars.find((cal) => cal.id === calendar.eventTypeId);
				const calendarName = selectedCalendar?.name || '';

				await onFinish({
					calendarType: 'gohighlevel',
					eventTypeId: calendar.eventTypeId,
					location: calendar.location,
					calendarSelected: calendarName,
					scenario: calendar.scenario,
					sendCallerSMSNotification: calendar.sendCallerSMSNotification ?? true,
					sendOwnerSMSNotification: calendar.sendOwnerSMSNotification ?? true,
				});

				if (isDuplicate) {
					message.success('Calendar has been duplicated successfully!');
				}
			} else if (selectedServiceType === 'housecallpro') {
				if (!calendar.apiKey) {
					message.error('Please enter your Housecall Pro API key');
					return;
				}
				const selectedFeatures = calendar.selectedFeatures || [];
				if (selectedFeatures.length === 0) {
					message.error('Please select at least one feature');
					return;
				}
				try {
					await createHousecallProWorkflow(
						calendar.apiKey,
						selectedFeatures,
						calendar.appointmentDuration || 2,
					);
					dispatch(closeModal());

					form.resetFields();
					setCurrentStep(0);
					setSelectedCalendarType(null);
					setSelectedServiceType(null);
					setEventTypes([]);
					setSelectedEventLocations({});

					const apiWorkflowElement = document.querySelector('[data-key="developer-automations"]');
					if (apiWorkflowElement) {
						apiWorkflowElement.scrollIntoView({ behavior: 'smooth' });
						(apiWorkflowElement as HTMLElement).click();

						setTimeout(() => {
							const apiIntegrationElement = document.querySelector('[data-key="api-workflow"]');
							if (apiIntegrationElement) {
								(apiIntegrationElement as HTMLElement).click();
							}
						}, 500);
					}
				} catch (error) {
					console.error('Error creating Housecall Pro workflows:', error);
					message.error('Failed to create Housecall Pro workflows');
				}
				return;
			} else {
				if (!calendar.apiKey) {
					message.error('Please enter your Cal.com API key');
					return;
				}
				if (!calendar.eventTypeId) {
					message.error('Please select an event type');
					return;
				}
				if (!calendar.location) {
					message.error('Please select a meeting location');
					return;
				}

				await onFinish({
					calendarType: 'calcom',
					apiKey: calendar.apiKey,
					eventTypeId: calendar.eventTypeId,
					location: calendar.location,
					scenario: calendar.scenario,
					sendCallerSMSNotification: calendar.sendCallerSMSNotification ?? true,
					sendOwnerSMSNotification: calendar.sendOwnerSMSNotification ?? true,
					customFields: calendar.customFields || [],
					calendarSelected: '',
					additionalUserInfoFields: calendar.additionalUserInfoFields || [],
				});

				if (isDuplicate) {
					message.success('Calendar has been duplicated successfully!');
				}
			}

			dispatch(incrementScenarioCount(WorkflowType.CALENDAR));

			form.resetFields();
			setCurrentStep(0);
			setSelectedCalendarType(null);
			setSelectedServiceType(null);
			setEventTypes([]);
			setSelectedEventLocations({});
		} catch (error) {
			console.error('Validation failed:', error);
			message.error('Please fill in all required fields');
		}
	};

	const steps = [
		{
			title: 'Services',
			content: (
				<div className="step-content">
					<h2 className="step-title">Welcome to Calendar Setup</h2>
					<Text className="step-description">
						Select services to connect your calendar and start scheduling meetings
					</Text>
					<div className="service-cards">
						<div
							className={`service-card cursor-pointer ${selectedCalendarType === 'google' ? 'selected' : ''}`}
							onClick={() => {
								setSelectedServiceType('calcom');
								setSelectedCalendarType('google');
							}}
						>
							<div className="service-logo-container">
								<SiGooglecalendar size={32} />
							</div>
							<div className="service-desc">Connect Google Calendar</div>
						</div>

						<div
							className={`service-card cursor-pointer ${selectedCalendarType === 'outlook' ? 'selected' : ''}`}
							onClick={() => {
								setSelectedServiceType('calcom');
								setSelectedCalendarType('outlook');
							}}
						>
							<div className="service-logo-container">
								<PiMicrosoftOutlookLogoFill size={32} />
							</div>
							<div className="service-desc">Connect Outlook Calendar</div>
						</div>

						<div
							className={`service-card cursor-pointer ${selectedCalendarType === 'apple' ? 'selected' : ''}`}
							onClick={() => {
								setSelectedServiceType('calcom');
								setSelectedCalendarType('apple');
							}}
						>
							<div className="service-logo-container">
								<FaApple size={32} />
							</div>
							<div className="service-desc">Connect Apple Calendar</div>
						</div>

						<div
							className={`service-card cursor-pointer ${selectedServiceType === 'gohighlevel' ? 'selected' : ''}`}
							onClick={() => {
								setSelectedServiceType('gohighlevel');
								setSelectedCalendarType(null);
							}}
						>
							<div className="service-logo-container">
								<img src={GohighlevelLogo} alt="Gohighlevel" className="service-logo" />
							</div>
							<div className="service-desc">Connect GoHighLevel calendar</div>
						</div>

						{isAPIWorkflowEnabled && (
							<div
								className={`service-card cursor-pointer ${selectedServiceType === 'housecallpro' ? 'selected' : ''}`}
								onClick={() => {
									setSelectedServiceType('housecallpro');
									setSelectedCalendarType(null);
								}}
							>
								<div className="service-logo-container">
									<img src={HousecallproLogo} alt="housecallpro" className="service-logo" />
								</div>
								<div className="service-desc">Connect Housecall Pro calendar</div>
							</div>
						)}
					</div>
					<div className="step-actions">
						<Button
							type="primary"
							className="next-button"
							onClick={handleNextStep}
							disabled={
								!(selectedServiceType && (selectedServiceType !== 'calcom' || selectedCalendarType))
							}
						>
							NEXT: CONNECT CALENDAR
						</Button>
					</div>
				</div>
			),
		},
		{
			title: 'Connect',
			content: (
				<div className="step-content">
					{selectedServiceType === 'gohighlevel' && (
						<div>
							<h2 className="step-title">Connect to GoHighLevel</h2>
							<Text className="step-description">
								Connect your GoHighLevel account to access your calendars
							</Text>
							<div className="ghl-connect-container my-8 text-center">
								{loading ? (
									<Spin size="large" />
								) : isGhlConnected ? (
									<div className="connection-success">
										<div className="mb-4">
											<Button size="large" className="connect-button" disabled>
												You are Connected to GoHighLevel
											</Button>
										</div>
									</div>
								) : (
									<Button
										type="primary"
										onClick={handleGhlConnect}
										size="large"
										className="connect-button"
									>
										Connect to GoHighLevel
									</Button>
								)}
							</div>
							<div className="step-actions">
								<Button onClick={() => setCurrentStep(0)}>Back</Button>
								<Button type="primary" onClick={handleNextStep}>
									NEXT: SELECT CALENDAR
								</Button>
							</div>
						</div>
					)}
					{selectedServiceType === 'housecallpro' && (
						// Housecall Pro Connect UI
						<div>
							<h2 className="step-title">Connect to Housecall Pro</h2>
							<Text className="step-description">
								Connect your Housecall Pro account to access your calendars
							</Text>

							<Form.List name="calendars" initialValue={[{}]}>
								{(fields) => (
									<>
										{fields.map((field) => (
											<Form.Item
												key={field.key}
												name={[field.name, 'apiKey']}
												rules={[
													{ required: true, message: 'Please enter your Housecallpro API key' },
												]}
											>
												<Input
													placeholder="Enter your Housecallpro API key"
													className="api-key-input"
												/>
											</Form.Item>
										))}
									</>
								)}
							</Form.List>

							<div className="help-text">
								<InfoCircleOutlined /> Where to find your API key?{' '}
								<a
									href="https://help.housecallpro.com/en/articles/8505035-api-overview#h_208764ce84"
									className="guide-link"
									target="_blank"
									rel="noopener noreferrer"
								>
									View guide
								</a>
							</div>

							<div className="step-actions">
								<Button onClick={() => setCurrentStep(0)}>Back</Button>
								<Button type="primary" onClick={handleHousecallproConnect} loading={loading}>
									NEXT: SELECT FEATURES
								</Button>
							</div>
						</div>
					)}
					{selectedServiceType === 'calcom' && (
						// Cal.com Connect UI
						<div>
							<h2 className="step-title">Enter your Cal.com API Key</h2>
							<Text className="step-description">
								This connects your {selectedCalendarType} calendar with our platform
							</Text>

							<Form.List name="calendars" initialValue={[{}]}>
								{(fields) => (
									<>
										{fields.map((field) => (
											<Form.Item
												key={field.key}
												name={[field.name, 'apiKey']}
												rules={[{ required: true, message: 'Please enter your Cal.com API key' }]}
											>
												<Input placeholder="Enter your Cal.com API key" className="api-key-input" />
											</Form.Item>
										))}
									</>
								)}
							</Form.List>

							<div className="help-text">
								<InfoCircleOutlined /> Where to find your API key?{' '}
								<a
									href="https://cal.com/docs/api-reference/v1/authentication"
									className="guide-link"
									target="_blank"
									rel="noopener noreferrer"
								>
									View guide
								</a>
							</div>

							<div className="step-actions">
								<Button onClick={() => setCurrentStep(0)}>Back</Button>
								<Button type="primary" onClick={handleCalcomConnect} loading={loading}>
									NEXT: SELECT EVENT
								</Button>
							</div>
						</div>
					)}
				</div>
			),
		},
		{
			title: 'Event',
			content: (
				<div className="step-content">
					<h2 className="step-title">
						{selectedServiceType === 'gohighlevel' ? 'Select Calendar' : 'Select Event Type'}
					</h2>
					<Text className="step-description">
						{selectedServiceType === 'gohighlevel'
							? 'Choose the calendar for scheduling meetings'
							: 'Choose the event type for scheduling meetings'}
					</Text>

					<Form.List name="calendars">
						{(fields) => (
							<>
								{fields.map((field) => (
									<React.Fragment key={field.key}>
										<Form.Item
											key={`${field.key}-scenario`}
											name={[field.name, 'scenario']}
											label={
												<div className="form-label">
													<span>Scenario</span>
												</div>
											}
											tooltip="The scenario based on which you want to trigger the scheduling"
											rules={[{ required: true, message: 'Please enter a scenario' }]}
										>
											<Input
												placeholder="e.g., Whenever the caller wants to book or schedule any meetings"
												className="scenario-input"
											/>
										</Form.Item>

										<Form.Item
											key={`${field.key}-event`}
											name={[field.name, 'eventTypeId']}
											label={
												<div className="form-label">
													<span>
														{selectedServiceType === 'gohighlevel' ? 'Calendar' : 'Event Type'}
													</span>
												</div>
											}
											tooltip={
												selectedServiceType === 'gohighlevel'
													? 'Select the calendar for scheduling meetings'
													: 'Select the event type for scheduling meetings'
											}
											rules={[
												{
													required: selectedServiceType !== 'housecallpro',
													message:
														selectedServiceType === 'gohighlevel'
															? 'Please select a calendar'
															: 'Please select an event type',
												},
											]}
										>
											{selectedServiceType === 'gohighlevel' ? (
												<Select
													placeholder="Select calendar"
													onChange={handleEventTypeChange}
													loading={isLoadingGhlCalendars}
													options={ghlCalendars.map((calendar) => ({
														value: calendar.id,
														label: `${calendar.name} (${calendar.id})`,
													}))}
													className="event-select"
												/>
											) : selectedServiceType === 'housecallpro' ? (
												<>
													{/* Feature Selection for Housecall Pro */}
													<div className="mb-4">
														<Form.Item
															name={[field.name, 'selectedFeatures']}
															initialValue={[]}
															label={
																<div className="form-label">
																	<span>Select Features</span>
																</div>
															}
															tooltip="Select the features you want to use for scheduling appointments"
															rules={[
																{
																	validator: (_, value) => {
																		if (!value || value.length === 0) {
																			return Promise.reject('Please select at least one feature');
																		}
																		return Promise.resolve();
																	},
																	message: 'At least one feature must be selected to proceed',
																},
															]}
														>
															<div className="space-y-4">
																{eventTypes.map((feature, index) => (
																	<div
																		key={index}
																		className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${
																			form
																				.getFieldValue(['calendars', 0, 'selectedFeatures'])
																				?.includes(feature.id)
																				? 'border-blue-500 shadow-md bg-blue-50'
																				: 'border-gray-200 hover:border-gray-300'
																		}`}
																		onClick={() => {
																			// Toggle selection when clicking anywhere in the feature box
																			const calendars = form.getFieldValue('calendars');
																			const selectedFeatures = calendars[0].selectedFeatures || [];
																			const isSelected = selectedFeatures.includes(feature.id);

																			if (isSelected) {
																				// Remove from selected features
																				calendars[0].selectedFeatures = selectedFeatures.filter(
																					(id: any) => id !== feature.id,
																				);
																			} else {
																				// Add to selected features
																				calendars[0].selectedFeatures = [
																					...selectedFeatures,
																					feature.id,
																				];
																			}

																			// If it's the first selection, also set the eventTypeId for backward compatibility
																			if (calendars[0].selectedFeatures.length === 1) {
																				calendars[0].eventTypeId = feature.name;
																			}

																			form.setFieldsValue({ calendars });
																		}}
																	>
																		<div className="flex items-center justify-between p-4 hover:bg-gray-50">
																			<div className="flex items-center">
																				<Checkbox
																					checked={form
																						.getFieldValue(['calendars', 0, 'selectedFeatures'])
																						?.includes(feature.id)}
																					onChange={(e) => {
																						e.stopPropagation();

																						const calendars = form.getFieldValue('calendars');
																						const selectedFeatures =
																							calendars[0].selectedFeatures || [];

																						if (e.target.checked) {
																							// Add to selected features if not already included
																							if (!selectedFeatures.includes(feature.id)) {
																								calendars[0].selectedFeatures = [
																									...selectedFeatures,
																									feature.id,
																								];
																							}
																						} else {
																							// Remove from selected features
																							calendars[0].selectedFeatures =
																								selectedFeatures.filter(
																									(id: any) => id !== feature.id,
																								);
																						}

																						// If it's the first selection, also set the eventTypeId for backward compatibility
																						if (calendars[0].selectedFeatures.length === 1) {
																							calendars[0].eventTypeId = feature.name;
																						}

																						form.setFieldsValue({ calendars });
																					}}
																					onClick={(e) => e.stopPropagation()} // Prevent the div's onClick from firing
																				/>
																				<span className="ml-2 font-medium">{feature.name}</span>
																			</div>
																		</div>
																	</div>
																))}
															</div>
														</Form.Item>
													</div>
												</>
											) : (
												<Select
													placeholder="Select event type"
													onChange={handleEventTypeChange}
													options={eventTypes.map((type) => ({ value: type.id, label: type.name }))}
													className="event-select"
												/>
											)}
										</Form.Item>
									</React.Fragment>
								))}
							</>
						)}
					</Form.List>

					<div className="step-actions">
						<Button onClick={() => setCurrentStep(1)}>Back</Button>
						<Button type="primary" onClick={handleNextStep}>
							{selectedServiceType === 'gohighlevel' ? 'FINISH SETUP' : 'NEXT: SETTINGS'}
						</Button>
					</div>
				</div>
			),
		},
		{
			title: 'Settings',
			content: (
				<div className="step-content">
					<h2 className="step-title">
						{selectedServiceType === 'housecallpro' ? 'Finish setup' : 'Settings'}
					</h2>
					<Text className="step-description">
						{selectedServiceType === 'housecallpro'
							? 'Complete the setup for Housecall Pro integration'
							: 'Configure additional settings for your calendar'}
					</Text>
					<Form.List name="calendars">
						{(fields) => (
							<>
								{fields.map((field, index) => (
									<React.Fragment key={field.key}>
										{selectedServiceType !== 'gohighlevel' &&
											selectedServiceType !== 'housecallpro' && (
												<div className="settings-section">
													<h3 className="section-title">Meeting Location</h3>
													<Text className="section-description">
														Where would you like to host your meetings?
													</Text>
													<Form.Item
														name={[field.name, 'location']}
														rules={[
															{ required: true, message: 'Please select a meeting location' },
														]}
													>
														<Radio.Group className="location-options">
															{selectedEventLocations[index]?.map((locationType) => (
																<Radio.Button
																	key={locationType}
																	value={locationType}
																	className="location-button"
																>
																	<span className="location-icon">
																		{getLocationIcon(locationType)}
																	</span>
																	<span className="location-name">
																		{getLocationDisplayName(locationType)}
																	</span>
																</Radio.Button>
															))}
														</Radio.Group>
													</Form.Item>
												</div>
											)}

										{selectedServiceType !== 'housecallpro' && (
											<div className="settings-section">
												<h3 className="section-title">Notification Preferences</h3>
												<Text className="section-description">
													Configure how notifications are sent for appointments
												</Text>
												<Form.Item
													name={[field.name, 'sendCallerSMSNotification']}
													valuePropName="checked"
													initialValue={true}
												>
													<div className="notification-option">
														<span>Send SMS to customers</span>
														<Switch defaultChecked />
													</div>
												</Form.Item>

												<Form.Item
													name={[field.name, 'sendOwnerSMSNotification']}
													valuePropName="checked"
													initialValue={true}
												>
													<div className="notification-option">
														<span>Send SMS to me</span>
														<Switch defaultChecked />
													</div>
												</Form.Item>
											</div>
										)}

										{selectedServiceType !== 'gohighlevel' &&
											selectedServiceType !== 'housecallpro' && (
												<div className="settings-section">
													<h3 className="section-title">Additional User Information</h3>
													<Text className="section-description">
														Select which additional information should be required from users when
														booking appointments.
													</Text>
													<Form.Item
														name={[field.name, 'additionalUserInfoFields']}
														initialValue={[]}
														tooltip="These fields are optional by default, check to make them required"
													>
														<Checkbox.Group className="w-full">
															<div className="flex justify-between w-full">
																<div>
																	<span className="font-bold text-sm">Email address</span>
																	<div className="text-gray-500 text-sm">
																		Allow users to receive booking confirmations via email
																	</div>
																</div>
																<Checkbox value="email" />
															</div>
															<div className="flex justify-between w-full">
																<div>
																	<span className="font-bold text-sm">Timezone</span>
																	<div className="text-gray-500 text-sm">
																		AI will ask user & discuss appointment times in the user's local
																		timezone, defaults to your timezone.
																	</div>
																</div>
																<Checkbox value="timezone" />
															</div>
														</Checkbox.Group>
													</Form.Item>
												</div>
											)}

										{/* Business Hours for Housecall Pro */}
										{selectedServiceType === 'housecallpro' && (
											<>
												{/* Appointment Duration for Housecall Pro */}
												<div className="settings-section">
													<h3 className="section-title">Appointment Duration</h3>
													<Text className="section-description">
														Set the default duration for Housecall Pro appointments
													</Text>
													<Form.Item name={[field.name, 'appointmentDuration']} initialValue={2}>
														<Select className="w-full">
															{[
																{ value: 0.5, label: '30 minutes' },
																{ value: 1, label: '1 hour' },
																{ value: 1.5, label: '1 hour 30 minutes' },
																{ value: 2, label: '2 hours' },
																{ value: 2.5, label: '2 hours 30 minutes' },
																{ value: 3, label: '3 hours' },
																{ value: 3.5, label: '3 hours 30 minutes' },
																{ value: 4, label: '4 hours' },
																{ value: 4.5, label: '4 hours 30 minutes' },
																{ value: 5, label: '5 hours' },
															].map((item, index) => (
																<Select.Option key={index} value={item.value}>
																	{item.label}
																</Select.Option>
															))}
														</Select>
													</Form.Item>
												</div>
											</>
										)}

										{/* Custom Fields Section */}
										{selectedServiceType !== 'gohighlevel' &&
											selectedServiceType !== 'housecallpro' && (
												<div className="settings-section">
													<h3 className="section-title">Custom Fields</h3>
													<Alert
														type="warning"
														message="Default Fields"
														description={(() => {
															const defaultadditionalUserInfoFields = [
																'Name',
																'Event Date',
																'Event Time',
															];
															if (additionalUserInfoFields.includes('email')) {
																defaultadditionalUserInfoFields.push('Email');
															}
															if (additionalUserInfoFields.includes('timezone')) {
																defaultadditionalUserInfoFields.push('Timezone');
															}

															const defaultOptionalFields = [];
															if (!additionalUserInfoFields.includes('email')) {
																defaultOptionalFields.push('Email');
															}
															if (!additionalUserInfoFields.includes('timezone')) {
																defaultOptionalFields.push('Timezone');
															}

															let extraField = '';
															if (
																['attendeeAddress', 'attendeeDefined', 'attendeeInPerson'].includes(
																	location,
																)
															) {
																extraField = 'Address';
															} else if (location === 'attendeePhone') {
																extraField = 'Phone';
															}
															return (
																<>
																	The following fields are handled by default and cannot be added as
																	custom fields. <br />
																	Required: <b>{defaultadditionalUserInfoFields.join(', ')}</b>
																	{defaultOptionalFields.length > 0 && (
																		<>
																			{' '}
																			| Optional: <b>{defaultOptionalFields.join(', ')}</b>
																		</>
																	)}
																	.
																	<br />
																	<b>
																		Make sure that all custom fields you add are also added with
																		same identifiers/name on cal.com.
																	</b>
																	{extraField && (
																		<>
																			, <b>{extraField}</b> will also be asked since this is a{' '}
																			{extraField === 'Address' ? 'in-person' : 'phone'} meeting.
																		</>
																	)}
																</>
															);
														})()}
														className="mb-4"
														showIcon
													/>
													<Text className="section-description">
														Collect additional information from users during scheduling
													</Text>

													<Form.List name={[field.name, 'customFields']}>
														{(fields, { add, remove }) => (
															<div className="custom-fields-container">
																{fields.length > 0 ? (
																	<div className="custom-fields-list">
																		{fields.map(({ key, name, ...restField }) => (
																			<div
																				key={key}
																				className="custom-field-row grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-center"
																			>
																				<Form.Item
																					{...restField}
																					name={[name, 'name']}
																					rules={[
																						{ required: true, message: 'Field name is required' },
																						{
																							validator: (_, value) => {
																								const normalizedValue = value?.toLowerCase().trim();
																								const defaultFields = [
																									'name',
																									'firstName',
																									'lastName',
																									'eventDate',
																									'eventTime',
																									'timezone',
																									'email',
																								];
																								if (
																									defaultFields.some(
																										(field) => field === normalizedValue,
																									)
																								) {
																									return Promise.reject(
																										new Error(
																											'This field is already included by default',
																										),
																									);
																								}
																								return Promise.resolve();
																							},
																						},
																					]}
																					className="mb-0"
																				>
																					<Input
																						placeholder="Field Name (e.g., Company Name)"
																						className="field-name-input"
																					/>
																				</Form.Item>
																				<div className="flex items-center">
																					<Form.Item
																						{...restField}
																						name={[name, 'description']}
																						rules={[
																							{
																								required: true,
																								message: 'Description is required',
																							},
																						]}
																						className="mb-0 flex-grow"
																					>
																						<Input
																							placeholder="Description (e.g., What is your company name?)"
																							className="field-description-input"
																						/>
																					</Form.Item>
																					<MinusCircleOutlined
																						onClick={() => remove(name)}
																						className="remove-field-button ml-2 flex-shrink-0"
																					/>
																				</div>
																			</div>
																		))}
																	</div>
																) : (
																	<div className="no-fields-message text-gray-500 italic mb-4">
																		<Button
																			type="dashed"
																			onClick={() => add()}
																			block
																			icon={<PlusOutlined />}
																		>
																			Add Custom Field
																		</Button>
																	</div>
																)}
															</div>
														)}
													</Form.List>
												</div>
											)}
									</React.Fragment>
								))}
							</>
						)}
					</Form.List>

					<div className="step-actions">
						<Button onClick={() => setCurrentStep(2)}>Back</Button>
						<Button type="primary" onClick={handleSubmit}>
							FINISH SETUP
						</Button>
					</div>
				</div>
			),
		},
	];

	return (
		<div className="calendar-setup-form">
			<Steps current={currentStep} items={steps} />
			<Form
				form={form}
				layout="vertical"
				initialValues={{
					calendars: [
						{
							apiKey: '',
							eventTypeId: '',
							location: null,
							scenario:
								'Whenever the caller wants to book or schedule any meetings or appointments.',
							sendCallerSMSNotification: true,
							sendOwnerSMSNotification: true,
							additionalUserInfoFields: [],
						},
					],
				}}
			>
				{steps[currentStep].content}
			</Form>
		</div>
	);
};
